/**
 * HeaderMediaThumbnail — 64x64 thumbnail with image/video auto-rotation.
 *
 * Sits left of the product title in the header. Shows the primary image
 * for 5 seconds, then crossfades to the primary video (muted, auto-play).
 * When the video ends, crossfades back to image. Loops until paused.
 *
 * Tap toggles pause/resume. Same rotation logic as the old sidebar
 * DocSidebarCoverMediaSection but in a compact square.
 */

import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ImageIcon } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const IMAGE_DISPLAY_MS = 5000;
const FADE_MS = 400;

interface HeaderMediaThumbnailProps {
  image: string | null;
  video: { url: string } | null;
  alt?: string;
  /** Override container classes (e.g. full-width on mobile PDP) */
  className?: string;
}

export function HeaderMediaThumbnail({ image, video, alt = "", className }: HeaderMediaThumbnailProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const sizeClass = isDesktop ? "h-28 w-28" : "w-full h-full";
  const [showVideo, setShowVideo] = useState(false);
  const [fading, setFading] = useState(false);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasImage = !!image;
  const hasVideo = !!video;
  const hasBoth = hasImage && hasVideo;

  // Clear any pending timer
  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Crossfade to video after image displays
  useEffect(() => {
    if (!hasBoth || paused || showVideo) return;

    timerRef.current = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setShowVideo(true);
        setFading(false);
        videoRef.current?.play().catch(() => {});
      }, FADE_MS / 2);
    }, IMAGE_DISPLAY_MS);

    return clearTimer;
  }, [hasBoth, paused, showVideo]);

  // When video ends, crossfade back to image
  const handleVideoEnded = () => {
    if (paused) return;
    setFading(true);
    setTimeout(() => {
      setShowVideo(false);
      setFading(false);
    }, FADE_MS / 2);
  };

  // Tap to pause/resume
  const handleClick = () => {
    if (!hasBoth) return;

    const next = !paused;
    setPaused(next);

    if (next) {
      clearTimer();
      if (showVideo) videoRef.current?.pause();
    } else {
      if (showVideo) {
        videoRef.current?.play().catch(() => {});
      }
      // If on image, the useEffect will restart the timer
    }
  };

  // Cleanup
  useEffect(() => clearTimer, []);

  // No media at all
  if (!hasImage && !hasVideo) {
    return (
      <div className={cn(sizeClass, "rounded-md bg-muted/50 flex items-center justify-center shrink-0", className)}>
        <ImageIcon className="h-6 w-6 text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        sizeClass, "rounded-md overflow-hidden bg-muted/50 shrink-0 relative",
        hasBoth && "cursor-pointer",
        className
      )}
      onClick={handleClick}
    >
      {/* Image layer */}
      {hasImage && (
        <img
          src={image}
          alt={alt}
          // no-referrer so LINE profile images (profile.line-scdn.net) load —
          // their CDN 403s hotlinks that send a Referer. Harmless for our own
          // CDN product images.
          referrerPolicy="no-referrer"
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity",
            fading ? "opacity-50" : !showVideo ? "opacity-100" : "opacity-0"
          )}
          style={{ transitionDuration: `${FADE_MS}ms` }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}

      {/* Video layer */}
      {hasVideo && (
        <video
          ref={videoRef}
          src={video.url}
          muted
          playsInline
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity",
            fading ? "opacity-50" : showVideo ? "opacity-100" : "opacity-0"
          )}
          style={{ transitionDuration: `${FADE_MS}ms` }}
          onEnded={handleVideoEnded}
          onError={() => setShowVideo(false)}
        />
      )}
    </div>
  );
}
